import { create } from 'zustand';
import { uid } from '../audio/decode';
import { engine } from '../audio/engine';
import { dropSample, invalidateSource } from '../audio/registry';
import type { PlayMode } from '../audio/sequencer';
import { buildSliceEdges, sliceCount } from '../audio/slice';
import type {
  Pattern,
  Project,
  Scene,
  SourceMeta,
  Step,
  Track,
} from '../types';
import { createProject, makeLinearPattern, makeScene, makeStep } from './defaults';
import { PATTERN_PRESETS, STRAIGHT_PRESET_ID } from './presets';

export type View = 'chop' | 'sequence' | 'song' | 'mix';

interface UiState {
  view: View;
  selectedTrackId: string | null;
  selectedSourceId: string | null;
  selectedSteps: string[];
  playMode: PlayMode;
  playing: boolean;
  busy: string | null;
  toast: string | null;
}

interface StoreState extends UiState {
  project: Project;
  past: Project[];
  future: Project[];

  // --- infrastructure
  set: (fn: (p: Project) => void, historyLabel?: string | false) => void;
  undo: () => void;
  redo: () => void;
  setUi: (patch: Partial<UiState>) => void;
  notify: (msg: string | null) => void;

  // --- sources
  addSource: (meta: SourceMeta) => void;
  /**
   * Adds a loop and, when `assign` is set, drops it on the first free track
   * with a 1:1 pattern — so an import makes a sound instead of leaving you
   * looking at an empty grid.
   */
  importSource: (meta: SourceMeta, assign?: boolean) => void;
  updateSource: (id: string, patch: Partial<SourceMeta>) => void;
  removeSource: (id: string) => void;
  rechop: (id: string) => void;

  // --- patterns
  addPattern: (p: Pattern) => void;
  updatePattern: (id: string, patch: Partial<Pattern>) => void;
  duplicatePattern: (id: string) => string | null;
  removePattern: (id: string) => void;
  /** Drops every pattern no track and no scene clip refers to. */
  removeUnusedPatterns: () => void;
  patternFromSource: (sourceId: string, name?: string) => string | null;

  // --- steps
  updateStep: (patternId: string, stepId: string, patch: Partial<Step>) => void;
  updateSteps: (patternId: string, stepIds: string[], patch: Partial<Step>) => void;
  moveStep: (patternId: string, from: number, to: number) => void;
  duplicateStep: (patternId: string, index: number) => void;
  deleteStep: (patternId: string, index: number) => void;
  insertRest: (patternId: string, index: number) => void;
  setPatternLength: (patternId: string, length: number) => void;
  /**
   * Rewrites the whole slice order from a list (-1 = rest). Existing steps keep
   * their per-step settings by position, so retyping the order doesn't wipe
   * gates, pitches and retriggers you already dialled in.
   */
  setPatternOrder: (patternId: string, slices: number[]) => void;

  // --- pattern transforms
  transform: (patternId: string, op: TransformOp, arg?: number) => void;
  /**
   * Rebuilds a pattern from a preset generator. With `layer` set, the preset
   * stacks onto the current pattern instead of starting from a straight chop.
   */
  applyPreset: (patternId: string, presetId: string, layer?: boolean) => void;

  // --- tracks
  updateTrack: (id: string, patch: Partial<Track>) => void;
  assignSource: (trackId: string, sourceId: string | null) => void;
  assignPattern: (trackId: string, patternId: string | null) => void;
  addTrack: () => void;
  removeTrack: (id: string) => void;
  /** Empties a track back to defaults, keeping its name and colour. */
  clearTrack: (id: string) => void;
  clearAllTracks: () => void;

  // --- scenes
  addScene: () => void;
  duplicateScene: (id: string) => void;
  removeScene: (id: string) => void;
  updateScene: (id: string, patch: Partial<Scene>) => void;
  /** `undefined` clears the entry, so the scene follows the track's pattern. */
  setClip: (sceneId: string, trackId: string, patternId: string | null | undefined) => void;
  moveScene: (from: number, to: number) => void;

  loadProject: (p: Project) => void;
}

export type TransformOp =
  | 'reverse-order'
  | 'rotate-left'
  | 'rotate-right'
  | 'shuffle'
  | 'reverse-all'
  | 'reverse-alt'
  | 'invert-mutes'
  | 'clear'
  | 'linear'
  | 'double'
  | 'half'
  | 'humanize'
  | 'random-slices'
  | 'stutter-last'
  | 'thin';

const HISTORY_LIMIT = 100;

/** Everything a track forgets when you clear it. */
function blankTrack(): Omit<Track, 'id' | 'name' | 'color'> {
  return {
    sourceId: null,
    patternId: null,
    volume: 0.85,
    pan: 0,
    mute: false,
    solo: false,
    warp: 'stretch',
    pitch: 0,
    filter: 'off',
    cutoff: 12000,
    resonance: 1,
    drive: 0,
    sends: { reverb: 0, delay: 0 },
  };
}

function clone(p: Project): Project {
  return structuredClone(p);
}

export const useStore = create<StoreState>((setState, getState) => {
  /** Applies a mutation to a draft copy and optionally records history. */
  const mutate = (fn: (p: Project) => void, history: string | false = 'edit') => {
    const { project, past } = getState();
    const draft = clone(project);
    fn(draft);
    setState({
      project: draft,
      past: history === false ? past : [...past, project].slice(-HISTORY_LIMIT),
      future: history === false ? getState().future : [],
    });
    engine.update(draft);
  };

  return {
    project: createProject(),
    past: [],
    future: [],
    view: 'chop',
    selectedTrackId: null,
    selectedSourceId: null,
    selectedSteps: [],
    playMode: 'pattern',
    playing: false,
    busy: null,
    toast: null,

    set: mutate,

    undo: () => {
      const { past, project, future } = getState();
      if (!past.length) return;
      const prev = past[past.length - 1];
      setState({ project: prev, past: past.slice(0, -1), future: [project, ...future] });
      engine.update(prev);
    },

    redo: () => {
      const { past, project, future } = getState();
      if (!future.length) return;
      const next = future[0];
      setState({ project: next, past: [...past, project], future: future.slice(1) });
      engine.update(next);
    },

    setUi: (patch) => setState(patch as Partial<StoreState>),
    notify: (toast) => setState({ toast }),

    // ---------------------------------------------------------------- sources
    addSource: (meta) => {
      mutate((p) => {
        p.sources.push(meta);
        // First import seeds the project tempo — after that, imports conform.
        if (p.sources.length === 1 && meta.bpmConfidence > 0.15) {
          p.bpm = Math.round(meta.bpm * 10) / 10;
        }
      });
      setState({ selectedSourceId: meta.id });
    },

    importSource: (meta, assign = true) => {
      getState().addSource(meta);
      if (!assign) return;
      const state = getState();
      const empty = state.project.tracks.find((t) => !t.sourceId);
      if (!empty) return;
      state.assignSource(empty.id, meta.id);
      const pid = getState().patternFromSource(meta.id, meta.name);
      if (pid) getState().assignPattern(empty.id, pid);
      setState({ selectedTrackId: empty.id });
    },

    updateSource: (id, patch) => {
      mutate((p) => {
        const s = p.sources.find((x) => x.id === id);
        if (!s) return;
        Object.assign(s, patch);
        s.sliceEdges = buildSliceEdges(s, p.beatsPerBar);
      });
      invalidateSource(id);
    },

    rechop: (id) => {
      mutate((p) => {
        const s = p.sources.find((x) => x.id === id);
        if (s) s.sliceEdges = buildSliceEdges(s, p.beatsPerBar);
      }, false);
      invalidateSource(id);
    },

    removeSource: (id) => {
      mutate((p) => {
        p.sources = p.sources.filter((s) => s.id !== id);
        for (const t of p.tracks) if (t.sourceId === id) t.sourceId = null;
      });
      dropSample(id);
      if (getState().selectedSourceId === id) setState({ selectedSourceId: null });
    },

    // --------------------------------------------------------------- patterns
    addPattern: (pattern) => mutate((p) => void p.patterns.push(pattern)),

    updatePattern: (id, patch) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === id);
        if (pat) Object.assign(pat, patch);
      }),

    duplicatePattern: (id) => {
      const src = getState().project.patterns.find((x) => x.id === id);
      if (!src) return null;
      const copy: Pattern = {
        ...structuredClone(src),
        id: uid('pat'),
        name: `${src.name} copy`,
        steps: src.steps.map((s) => ({ ...s, id: uid('st') })),
      };
      mutate((p) => void p.patterns.push(copy));
      return copy.id;
    },

    removePattern: (id) =>
      mutate((p) => {
        p.patterns = p.patterns.filter((x) => x.id !== id);
        for (const t of p.tracks) if (t.patternId === id) t.patternId = null;
        for (const sc of p.scenes) {
          for (const k of Object.keys(sc.clips)) {
            if (sc.clips[k].patternId === id) sc.clips[k] = { patternId: null };
          }
        }
      }),

    removeUnusedPatterns: () =>
      mutate((p) => {
        const unused = new Set(unusedPatternIds(p));
        if (!unused.size) return;
        p.patterns = p.patterns.filter((x) => !unused.has(x.id));
      }),

    patternFromSource: (sourceId, name) => {
      const { project } = getState();
      const src = project.sources.find((s) => s.id === sourceId);
      if (!src) return null;
      const count = sliceCount(src, project.beatsPerBar);
      const pattern = makeLinearPattern(
        name ?? src.name,
        Math.min(64, count),
        src.division,
        src.color,
      );
      mutate((p) => void p.patterns.push(pattern));
      return pattern.id;
    },

    // ------------------------------------------------------------------ steps
    updateStep: (patternId, stepId, patch) =>
      mutate((p) => {
        const s = p.patterns.find((x) => x.id === patternId)?.steps.find((x) => x.id === stepId);
        if (s) Object.assign(s, patch);
      }),

    updateSteps: (patternId, stepIds, patch) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        if (!pat) return;
        for (const s of pat.steps) if (stepIds.includes(s.id)) Object.assign(s, patch);
      }),

    moveStep: (patternId, from, to) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        if (!pat) return;
        if (from < 0 || from >= pat.steps.length) return;
        const clamped = Math.max(0, Math.min(pat.steps.length - 1, to));
        const [moved] = pat.steps.splice(from, 1);
        pat.steps.splice(clamped, 0, moved);
      }),

    duplicateStep: (patternId, index) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        if (!pat) return;
        const s = pat.steps[index];
        if (!s) return;
        pat.steps.splice(index + 1, 0, { ...structuredClone(s), id: uid('st') });
        pat.length = pat.steps.length;
      }),

    deleteStep: (patternId, index) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        if (!pat || pat.steps.length <= 1) return;
        pat.steps.splice(index, 1);
        pat.length = pat.steps.length;
      }),

    insertRest: (patternId, index) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        if (!pat) return;
        pat.steps.splice(index, 0, makeStep(-1));
        pat.length = pat.steps.length;
      }),

    setPatternLength: (patternId, length) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        if (!pat) return;
        const next = Math.max(1, Math.min(256, Math.round(length)));
        while (pat.steps.length < next) {
          const mirror = pat.steps[pat.steps.length % Math.max(1, pat.steps.length)];
          pat.steps.push(mirror ? { ...structuredClone(mirror), id: uid('st') } : makeStep(-1));
        }
        pat.steps.length = next;
        pat.length = next;
      }),

    setPatternOrder: (patternId, slices) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        if (!pat || !slices.length) return;
        const next = slices.slice(0, 256).map((slice, i) => {
          const existing = pat.steps[i];
          return existing
            ? { ...existing, slice }
            : { ...makeStep(slice), ...structuredClone(pat.steps[i % pat.steps.length] ?? {}), id: uid('st'), slice };
        });
        pat.steps = next;
        pat.length = next.length;
      }),

    applyPreset: (patternId, presetId, layer = false) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        const preset = PATTERN_PRESETS.find((x) => x.id === presetId);
        if (!pat || !preset) return;
        // Slice count comes from whichever track is driving this pattern, so a
        // preset always fits the loop actually loaded.
        const track = p.tracks.find((t) => t.patternId === patternId);
        const src = p.sources.find((s) => s.id === track?.sourceId);
        const slices = src ? sliceCount(src, p.beatsPerBar) : pat.length;
        pat.steps = preset.build({
          length: pat.length,
          slices,
          division: pat.division,
          beatsPerBar: p.beatsPerBar,
          steps: pat.steps,
          // Straight is the reset — it never layers, whatever the toggle says.
          layer: layer && preset.id !== STRAIGHT_PRESET_ID,
        });
        pat.length = pat.steps.length;
      }),

    transform: (patternId, op, arg) =>
      mutate((p) => {
        const pat = p.patterns.find((x) => x.id === patternId);
        if (!pat) return;
        applyTransform(p, pat, op, arg);
      }),

    // ----------------------------------------------------------------- tracks
    updateTrack: (id, patch) =>
      mutate((p) => {
        const t = p.tracks.find((x) => x.id === id);
        if (t) Object.assign(t, patch);
      }),

    assignSource: (trackId, sourceId) =>
      mutate((p) => {
        const t = p.tracks.find((x) => x.id === trackId);
        if (t) t.sourceId = sourceId;
      }),

    assignPattern: (trackId, patternId) =>
      mutate((p) => {
        const t = p.tracks.find((x) => x.id === trackId);
        if (t) t.patternId = patternId;
      }),

    addTrack: () =>
      mutate((p) => {
        const idx = p.tracks.length;
        const t: Track = {
          ...structuredClone(p.tracks[0] ?? {}),
          id: uid('trk'),
          name: `Track ${idx + 1}`,
          sourceId: null,
          patternId: null,
          mute: false,
          solo: false,
          color: ['#ff2d95', '#38bdf8', '#fbbf24', '#5eead4', '#a78bfa', '#fb7185'][idx % 6],
        } as Track;
        p.tracks.push(t);
      }),

    removeTrack: (id) => {
      const { project, selectedTrackId } = getState();
      if (project.tracks.length <= 1) return;
      const index = project.tracks.findIndex((t) => t.id === id);
      mutate((p) => {
        p.tracks = p.tracks.filter((t) => t.id !== id);
        for (const sc of p.scenes) delete sc.clips[id];
      });
      // Leaving the selection pointing at a track that no longer exists is the
      // worst outcome: the strip highlights nothing while the sequencer quietly
      // falls back to the first track, so edits land somewhere you aren't
      // looking. Move to the track that took its place.
      if (selectedTrackId === id) {
        const tracks = getState().project.tracks;
        const next = tracks[Math.min(index, tracks.length - 1)];
        setState({ selectedTrackId: next?.id ?? null, selectedSteps: [] });
      }
    },

    clearTrack: (id) => {
      mutate((p) => {
        const t = p.tracks.find((x) => x.id === id);
        if (!t) return;
        // Patterns live in a shared pool and may be used by other tracks or
        // scenes, so clearing unassigns rather than deletes.
        Object.assign(t, blankTrack(), { id: t.id, name: t.name, color: t.color });
        for (const sc of p.scenes) delete sc.clips[id];
      });
      // Only drop the step selection if it belonged to the track being cleared.
      if (getState().selectedTrackId === id) setState({ selectedSteps: [] });
    },

    clearAllTracks: () => {
      mutate((p) => {
        for (const t of p.tracks) {
          Object.assign(t, blankTrack(), { id: t.id, name: t.name, color: t.color });
        }
        for (const sc of p.scenes) sc.clips = {};
      });
      setState({ selectedSteps: [] });
    },

    // ----------------------------------------------------------------- scenes
    addScene: () =>
      mutate((p) => {
        const name = String.fromCharCode(65 + (p.scenes.length % 26));
        p.scenes.push(makeScene(name, 4));
      }),

    duplicateScene: (id) =>
      mutate((p) => {
        const i = p.scenes.findIndex((s) => s.id === id);
        if (i < 0) return;
        const copy = structuredClone(p.scenes[i]);
        copy.id = uid('scn');
        copy.name = `${copy.name}'`;
        p.scenes.splice(i + 1, 0, copy);
      }),

    removeScene: (id) =>
      mutate((p) => {
        if (p.scenes.length <= 1) return;
        p.scenes = p.scenes.filter((s) => s.id !== id);
      }),

    updateScene: (id, patch) =>
      mutate((p) => {
        const s = p.scenes.find((x) => x.id === id);
        if (s) Object.assign(s, patch);
      }),

    setClip: (sceneId, trackId, patternId) =>
      mutate((p) => {
        const s = p.scenes.find((x) => x.id === sceneId);
        if (!s) return;
        if (patternId === undefined) delete s.clips[trackId];
        else s.clips[trackId] = { patternId };
      }),

    moveScene: (from, to) =>
      mutate((p) => {
        if (from < 0 || from >= p.scenes.length) return;
        const clamped = Math.max(0, Math.min(p.scenes.length - 1, to));
        const [s] = p.scenes.splice(from, 1);
        p.scenes.splice(clamped, 0, s);
      }),

    loadProject: (p) => {
      setState({ project: p, past: [], future: [], selectedSteps: [] });
      engine.update(p);
    },
  };
});

// ---------------------------------------------------------------- transforms

function applyTransform(project: Project, pat: Pattern, op: TransformOp, arg?: number): void {
  const steps = pat.steps;
  const rnd = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
  const maxSlice = (() => {
    const track = project.tracks.find((t) => t.patternId === pat.id);
    const src = project.sources.find((s) => s.id === track?.sourceId);
    return src ? sliceCount(src, project.beatsPerBar) - 1 : 15;
  })();

  switch (op) {
    case 'reverse-order':
      steps.reverse();
      break;
    case 'rotate-left':
      steps.push(steps.shift()!);
      break;
    case 'rotate-right':
      steps.unshift(steps.pop()!);
      break;
    case 'shuffle':
      for (let i = steps.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [steps[i], steps[j]] = [steps[j], steps[i]];
      }
      break;
    case 'reverse-all':
      for (const s of steps) s.reverse = !s.reverse;
      break;
    case 'reverse-alt':
      steps.forEach((s, i) => {
        if (i % 2 === 1) s.reverse = !s.reverse;
      });
      break;
    case 'invert-mutes':
      for (const s of steps) s.mute = !s.mute;
      break;
    case 'clear':
      for (const s of steps) {
        s.slice = -1;
        s.mute = false;
      }
      break;
    case 'linear':
      steps.forEach((s, i) => {
        s.slice = i % (maxSlice + 1);
        s.mute = false;
        s.reverse = false;
      });
      break;
    case 'double': {
      // Same material, half the time: play every other step, twice as fast.
      const half = steps.filter((_, i) => i % 2 === 0);
      pat.steps = [...half, ...half.map((s) => ({ ...structuredClone(s), id: uid('st') }))];
      pat.length = pat.steps.length;
      break;
    }
    case 'half': {
      const out: Step[] = [];
      for (const s of steps) {
        out.push(s, { ...structuredClone(s), id: uid('st'), slice: -1 });
      }
      pat.steps = out.slice(0, 256);
      pat.length = pat.steps.length;
      break;
    }
    case 'humanize':
      for (const s of steps) {
        s.nudge = Math.max(-0.5, Math.min(0.5, (Math.random() - 0.5) * 0.12));
        s.gain = Math.max(0, Math.min(2, 1 + (Math.random() - 0.5) * 0.25));
      }
      break;
    case 'random-slices':
      for (const s of steps) s.slice = Math.floor(Math.random() * (maxSlice + 1));
      break;
    case 'stutter-last': {
      const n = arg ?? 4;
      const tail = steps.slice(-2);
      for (const s of tail) {
        s.repeat = n;
        s.repeatRamp = -0.4;
      }
      break;
    }
    case 'thin':
      steps.forEach((s, i) => {
        if (i % 2 === 1) s.mute = rnd([true, false, true]);
      });
      break;
  }
  pat.length = pat.steps.length;
}

// -------------------------------------------------------------- derived reads

export function useProject(): Project {
  return useStore((s) => s.project);
}

/**
 * Patterns nothing points at.
 *
 * Importing a loop auto-creates a 1:1 pattern for it, and clearing a track or
 * swapping its pattern unassigns rather than deletes — patterns are a shared
 * pool, so a pattern dropped by one track may still be driving another or a
 * scene. That's correct, but it means orphans pile up in the dropdown over a
 * long session.
 */
export function unusedPatternIds(project: Project): string[] {
  const used = new Set<string>();
  for (const t of project.tracks) if (t.patternId) used.add(t.patternId);
  for (const sc of project.scenes) {
    for (const clip of Object.values(sc.clips)) {
      if (clip?.patternId) used.add(clip.patternId);
    }
  }
  return project.patterns.filter((p) => !used.has(p.id)).map((p) => p.id);
}

export function patternOf(project: Project, track: Track | null): Pattern | null {
  if (!track) return null;
  return project.patterns.find((p) => p.id === track.patternId) ?? null;
}

export function sourceOf(project: Project, track: Track | null): SourceMeta | null {
  if (!track) return null;
  return project.sources.find((s) => s.id === track.sourceId) ?? null;
}
